'use client';

import { c as _c } from "react/compiler-runtime";
import type { ChainId, MarketId } from '@morpho-org/blue-sdk';
import type { InterfaceToken, MarketForRewards } from '@morpho-org/dapp-core';
import { ZERO } from '@morpho-org/dapp-core';
import { Avatar } from '@morpho-org/ui-v6/client';
import { Tag } from '@morpho-org/ui-v6/server';
import { getMarketNameParts } from '@morpho-org/ui-v6/utils';
import { Separator, TruncatedTooltip } from '@repo/ui';
import { memo } from 'react';
import { IconRewardHover } from '@/components/common/IconRewardHover';
import { LinkIcon } from '@/components/common/LinkIcon';
import { useMarketLendSummaryData } from '@/components/common/MarketLendSummary';
import { useWeb3Context } from '@/contexts/Web3Context';
import { formatLtv } from '@/helpers/formatters';
import { getMarketUrl } from '@/helpers/routing';
import { RecapPositionRow } from './MarketRecapV2';
import { RecapContainer } from './RecapContainer';
import RecapInteraction from './RecapInteraction';
export interface MarketLendWithdrawRecapV2Props {
  marketId: MarketId;
  chainId: ChainId;
  collateralAsset?: InterfaceToken | null;
  loanAsset: InterfaceToken;
  /** Loan assets moving in this action. */
  amount: bigint;
  /** Interaction-row verb; `Withdraw` on an exit, `Lend` on a supply. */
  actionLabel?: 'Lend' | 'Withdraw';
  /** Current on-chain loan-asset supply. */
  initialSupply: bigint;
  /** Supply after the action; `undefined` while the simulation resolves. */
  finalSupply: bigint | undefined;
  /** Market liquidation LTV (WAD) — static, part of the market identity header. */
  lltv: bigint;
  /** APY + rewards source (same as the lend summary card), for the APY row. */
  marketForRewards?: MarketForRewards | null;
  avgSupplyApy?: number | null;
  /** Skeleton in place of the before-value while the current on-chain position loads. */
  isInitialPositionLoading?: boolean;
  /** Skeleton in place of the after-value while the EVM simulation resolves. */
  isFinalPositionLoading?: boolean;
  /** After-value is a JS-math estimate (simulation unavailable/unverified). */
  isSimulationFallback?: boolean;
}

/**
 * Lender recap — the supply-side sister of `MarketRecapV2`, shared by lend and
 * lend-withdraw. Same market identity header (avatars + name + LLTV tag + open-market
 * link), then the Lend/Withdraw interaction row and the Lent before → after + net supply APY rows; the
 * borrower rows (Collateral / Loan / LTV / Liq. LTV) don't apply to a lender.
 * Position figures + APY reuse `useMarketLendSummaryData`, so the recap and the
 * withdraw dialog's summary card stay consistent.
 */
const MarketLendWithdrawRecapV2 = t0 => {
  const $ = _c(79);
  const {
    marketId,
    chainId,
    collateralAsset,
    loanAsset,
    amount,
    actionLabel: t1,
    initialSupply,
    finalSupply,
    lltv,
    marketForRewards,
    avgSupplyApy,
    isInitialPositionLoading,
    isFinalPositionLoading,
    isSimulationFallback
  } = t0;
  const actionLabel = t1 === undefined ? "Withdraw" : t1;
  const {
    targetChainIdentifier
  } = useWeb3Context();
  let t2;
  if ($[0] !== avgSupplyApy || $[1] !== finalSupply || $[2] !== initialSupply || $[3] !== loanAsset || $[4] !== marketForRewards) {
    t2 = {
      initialSupply,
      finalSupply,
      loanAsset,
      avgSupplyApy,
      marketForRewards
    };
    $[0] = avgSupplyApy;
    $[1] = finalSupply;
    $[2] = initialSupply;
    $[3] = loanAsset;
    $[4] = marketForRewards;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const {
    supply,
    apy: apyData
  } = useMarketLendSummaryData(t2);
  let t3;
  if ($[6] !== collateralAsset || $[7] !== lltv || $[8] !== loanAsset) {
    t3 = getMarketNameParts({
      loanAsset,
      collateralAsset,
      lltv
    });
    $[6] = collateralAsset;
    $[7] = lltv;
    $[8] = loanAsset;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const {
    loanSymbol,
    collateralSymbol
  } = t3;
  let t4;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      lineHeight: "normal"
    };
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== lltv) {
    t5 = formatLtv(lltv);
    $[11] = lltv;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== t5) {
    t6 = <Tag variant="bundler"><span className="text-3xs text-foreground" style={t4}>{t5}</span></Tag>;
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  const lltvTag = t6;
  let t7;
  if ($[15] !== loanAsset.logoURI) {
    t7 = <Avatar aria-hidden={true} src={loanAsset.logoURI} size="medium" />;
    $[15] = loanAsset.logoURI;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== loanSymbol) {
    t8 = ref => <span ref={ref} className="text-title-xs text-foreground max-w-[160px] min-w-0 truncate md:max-w-none">{loanSymbol}</span>;
    $[17] = loanSymbol;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== loanSymbol || $[20] !== t8) {
    t9 = <TruncatedTooltip text={loanSymbol}>{t8}</TruncatedTooltip>;
    $[19] = loanSymbol;
    $[20] = t8;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] !== t7 || $[23] !== t9) {
    t10 = <span className="gap-xs inline-flex min-w-0 items-center">{t7}{t9}</span>;
    $[22] = t7;
    $[23] = t9;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  let t11;
  if ($[25] !== collateralAsset || $[26] !== collateralSymbol || $[27] !== lltvTag) {
    t11 = collateralAsset ? <span className="gap-xs inline-flex min-w-0 items-center"><span aria-hidden={true} className="bg-muted-foreground h-lg w-0.5 shrink-0 rounded-full" /><span className="sr-only">backed by</span><span className="gap-xs inline-flex min-w-0 items-center"><Avatar aria-hidden={true} src={collateralAsset.logoURI} size="medium" /><TruncatedTooltip text={collateralSymbol ?? ""}>{ref_0 => <span ref={ref_0} className="text-title-xs text-foreground max-w-[160px] min-w-0 truncate md:max-w-none">{collateralSymbol}</span>}</TruncatedTooltip>{lltvTag}</span></span> : lltvTag;
    $[25] = collateralAsset;
    $[26] = collateralSymbol;
    $[27] = lltvTag;
    $[28] = t11;
  } else {
    t11 = $[28];
  }
  const t12 = collateralAsset?.symbol ?? "";
  const t13 = loanAsset.symbol ?? "";
  let t14;
  if ($[29] !== marketId || $[30] !== t12 || $[31] !== t13 || $[32] !== targetChainIdentifier) {
    t14 = getMarketUrl({
      identifier: targetChainIdentifier,
      marketId,
      collateralSymbol: t12,
      loanSymbol: t13
    });
    $[29] = marketId;
    $[30] = t12;
    $[31] = t13;
    $[32] = targetChainIdentifier;
    $[33] = t14;
  } else {
    t14 = $[33];
  }
  let t15;
  if ($[34] !== t14) {
    t15 = <LinkIcon tooltip="Open market." variant="outline" href={t14} />;
    $[34] = t14;
    $[35] = t15;
  } else {
    t15 = $[35];
  }
  let t16;
  if ($[36] !== t10 || $[37] !== t11 || $[38] !== t15) {
    t16 = <div className="gap-xs flex flex-wrap items-center">{t10}{t11}{t15}</div>;
    $[36] = t10;
    $[37] = t11;
    $[38] = t15;
    $[39] = t16;
  } else {
    t16 = $[39];
  }
  let t17;
  if ($[40] !== actionLabel || $[41] !== amount || $[42] !== loanAsset) {
    t17 = amount > ZERO && <><RecapInteraction amount={amount} asset={loanAsset} label={actionLabel} /><Separator /></>;
    $[40] = actionLabel;
    $[41] = amount;
    $[42] = loanAsset;
    $[43] = t17;
  } else {
    t17 = $[43];
  }
  const t18 = `Lent (${loanAsset.symbol})`;
  let t19;
  if ($[44] !== isFinalPositionLoading || $[45] !== isInitialPositionLoading || $[46] !== isSimulationFallback || $[47] !== supply.final || $[48] !== supply.finalDetailed || $[49] !== supply.initial || $[50] !== supply.initialDetailed || $[51] !== supply.showFinalValue || $[52] !== t18) {
    t19 = <RecapPositionRow label={t18} before={supply.initial} beforeTooltip={supply.initialDetailed} after={supply.final} afterTooltip={supply.finalDetailed} showAfter={supply.showFinalValue} isInitialPositionLoading={isInitialPositionLoading} isFinalPositionLoading={isFinalPositionLoading} showEstimateBadge={isSimulationFallback} />;
    $[44] = isFinalPositionLoading;
    $[45] = isInitialPositionLoading;
    $[46] = isSimulationFallback;
    $[47] = supply.final;
    $[48] = supply.finalDetailed;
    $[49] = supply.initial;
    $[50] = supply.initialDetailed;
    $[51] = supply.showFinalValue;
    $[52] = t18;
    $[53] = t19;
  } else {
    t19 = $[53];
  }
  let t20;
  if ($[54] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <span className="text-secondary-foreground text-xs">APY</span>;
    $[54] = t20;
  } else {
    t20 = $[54];
  }
  let t21;
  if ($[55] !== apyData.apy || $[56] !== apyData.netApy || $[57] !== apyData.rewards || $[58] !== loanAsset) {
    t21 = {
      apy: apyData.apy,
      rewards: apyData.rewards,
      variant: "supply",
      netApy: apyData.netApy,
      asset: loanAsset
    };
    $[55] = apyData.apy;
    $[56] = apyData.netApy;
    $[57] = apyData.rewards;
    $[58] = loanAsset;
    $[59] = t21;
  } else {
    t21 = $[59];
  }
  let t22;
  if ($[60] !== chainId || $[61] !== loanAsset.address || $[62] !== marketId) {
    t22 = {
      kind: "market",
      chainId,
      marketId,
      loanTokenAddress: loanAsset.address
    };
    $[60] = chainId;
    $[61] = loanAsset.address;
    $[62] = marketId;
    $[63] = t22;
  } else {
    t22 = $[63];
  }
  let t23;
  if ($[64] !== apyData.formattedNetApy) {
    t23 = <span className="text-foreground text-xs">{apyData.formattedNetApy}</span>;
    $[64] = apyData.formattedNetApy;
    $[65] = t23;
  } else {
    t23 = $[65];
  }
  let t24;
  if ($[66] !== t21 || $[67] !== t22 || $[68] !== t23) {
    t24 = <div className="flex items-center justify-between gap-[16px]">{t20}<IconRewardHover RewardsHoverCardProps={t21} merklContext={t22} isReversed={true} iconSize={20}>{t23}</IconRewardHover></div>;
    $[66] = t21;
    $[67] = t22;
    $[68] = t23;
    $[69] = t24;
  } else {
    t24 = $[69];
  }
  let t25;
  if ($[70] !== t19 || $[71] !== t24) {
    t25 = <div className="gap-sm flex flex-col items-stretch">{t19}{t24}</div>;
    $[70] = t19;
    $[71] = t24;
    $[72] = t25;
  } else {
    t25 = $[72];
  }
  let t26;
  if ($[73] !== t17 || $[74] !== t25) {
    t26 = <div className="gap-sm flex flex-col items-stretch">{t17}{t25}</div>;
    $[73] = t17;
    $[74] = t25;
    $[75] = t26;
  } else {
    t26 = $[75];
  }
  let t27;
  if ($[76] !== t16 || $[77] !== t26) {
    t27 = <RecapContainer className="gap-lg flex-col items-stretch">{t16}{t26}</RecapContainer>;
    $[76] = t16;
    $[77] = t26;
    $[78] = t27;
  } else {
    t27 = $[78];
  }
  return t27;
};
export default memo(MarketLendWithdrawRecapV2);